'use client';
import { FC, useMemo, useState } from 'react';
import { Map } from '@/components/shared';

import {
    AddressSrc,
    ICoordinate,
    ILocation,
    IRegion_SELL,
    ILocationObject,
} from '@/types';
import { findPointInPolygon, ILocationDropDown, ISmallArea } from '@/hook';
import { NEXT_APP_SYLNDR_GOOGLE_MAP_API_KEY } from '@/constants';
import { Libraries, useLoadScript } from '@react-google-maps/api';

// const libraries: MapLibraries = ['places', 'geometry'];
const libraries: Libraries = ['places', 'geometry'];

interface IMapComponentProps {
    regions: ILocationDropDown[];
    region: IRegion_SELL;
    step: 'region';
    formFunction: (key: 'region', clickedItem: IRegion_SELL) => void;
    open: boolean;
    setOpenMap?: (isOpen: boolean) => any;
}

const LocationMap: FC<IMapComponentProps> = ({
    regions,
    region,
    formFunction,
    step,
    setOpenMap,
}) => {
    const defaultLoc: ILocation = {
        city: region?.addressCity,
        address: region?.address,
        coordinate: region?.coordinate || { lat: 30.0444, lng: 31.2357 },
    };

    const [location, setLocation] = useState<ILocation>(defaultLoc);
    const [addressSrc, setAddressSrc] =
        useState<AddressSrc>('reverseGeolocation');

    const formatPolygons = useMemo<ISmallArea[]>(() => {
        const areas: ISmallArea[] = [];
        regions.forEach((city: ILocationDropDown) => {
            areas.push(...city.smallArea);
        });
        return areas;
    }, [region, regions]);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: NEXT_APP_SYLNDR_GOOGLE_MAP_API_KEY,
        libraries: libraries,
    });
    const getCenter = (polygon: ICoordinate[]): ICoordinate => {
        const summed = polygon.reduce(
            (prev, curr) => {
                return { lat: prev.lat + curr.lat, lng: prev.lng + curr.lng };
            },
            { lat: 0, lng: 0 }
        );
        return {
            lat: summed.lat / polygon.length,
            lng: summed.lng / polygon.length,
        };
    };
    const buildRegionObj = (locationObj: ILocationObject) => {
        const regionObj: IRegion_SELL = {
            id: locationObj.id,
            bigArea: locationObj.bigArea.en,
            bigAreaAr: locationObj.bigArea.ar,
            smallArea: locationObj.smallArea.nameEn,
            smallAreaAr: locationObj.smallArea.nameAr,
            name: locationObj.smallArea.nameEn,
            address: locationObj.address,
            coordinate: locationObj.coordinate,
            polygon: locationObj.smallArea.polygon,
            warehouse: locationObj.smallArea.warehouse,
            serviceable: locationObj.smallArea.serviceable,
            addressCity: locationObj.addressCity,
            sendPinLocation: true,
        };
        formFunction(step, regionObj);
    };

    const handleSetLocation = (loc: ILocation) => {
        const currArea = findPointInPolygon(
            isLoaded,
            formatPolygons,
            loc?.coordinate
        );
        let tempLocationObject: ILocationObject = {
            bigArea: { en: '', ar: '' },
            smallArea: {
                id: '',
                nameEn: '',
                nameAr: '',
                selected: false,
                showAlert: false,
                polygon: [] as ICoordinate[],
                serviceable: false,
                warehouse: false,
            },
            address: loc.address,
            coordinate: loc.coordinate || { lat: 0, lng: 0 },
            addressCity: loc.city,
            id: '',
        };
        const value = currArea?.nameEn;
        regions.map(location => {
            const smallAreas = location.smallArea.map(smallArea => {
                const area: ISmallArea = {
                    ...smallArea,
                    selected: value === smallArea?.nameEn,
                };
                if (value === smallArea?.nameEn)
                    tempLocationObject = {
                        bigArea: {
                            en: location.bigAreaEn,
                            ar: location.bigAreaAr,
                        },
                        smallArea,
                        address: loc.address,
                        coordinate:
                            loc.coordinate || getCenter(smallArea.polygon),
                        addressCity: loc.city,
                        id: location.id,
                    };
                return area;
            });
            return {
                ...location,
                expanded: false,
                smallArea: [...smallAreas],
            };
        });
        setLocation(loc);
        if (setOpenMap) setOpenMap(false);
        buildRegionObj(tempLocationObject);
    };

    if (!isLoaded) return null;

    return (
        <>
            <Map
                setAddressSrc={setAddressSrc}
                addressSrc={addressSrc}
                onSetLocation={handleSetLocation}
                location={location}
            />
        </>
    );
};

export default LocationMap;
